import { createStore } from 'vuex';
import axios from 'axios';

import { getUserRole, hasPermission, hasRouteAccess } from '@/auth/permissions';
import { clearCompanySettingsCache, getCompanySettingsCached, setCompanySettingsCache } from '@/services/companySettings';

const store = createStore({
    state() {
        return {
            userDetails: null,
            permissions: [],
            role: null,
            currentLocation: null,
            locations: [],
            multiLocationEnabled: false,
            locationMode: 'SINGLE',
            companySettings: null,
            isAuthenticated: false,
            authResolved: false,
        };
    },
    mutations: {
        SET_AUTH_DATA(state, payload) {
            state.userDetails = payload?.UserAttributes || null;
            state.permissions = payload?.Permissions || [];
            state.role = payload?.Role || null;
            state.currentLocation = payload?.CurrentLocation || null;
            state.locations = payload?.Locations || [];
            state.multiLocationEnabled = !!payload?.MultiLocationEnabled;
            state.locationMode = payload?.LocationMode || 'SINGLE';
            state.isAuthenticated = true;
            state.authResolved = true;
        },
        CLEAR_AUTH_DATA(state) {
            state.userDetails = null;
            state.permissions = [];
            state.role = null;
            state.currentLocation = null;
            state.locations = [];
            state.multiLocationEnabled = false;
            state.locationMode = 'SINGLE';
            state.companySettings = null;
            state.isAuthenticated = false;
            state.authResolved = true;
            clearCompanySettingsCache();
        },
        SET_AUTH_RESOLVED(state, value) {
            state.authResolved = value;
        },
        SET_COMPANY_SETTINGS(state, value) {
            state.companySettings = value || {};
            setCompanySettingsCache(state.companySettings);
        },
    },
    actions: {
        async login({ commit }, credentials) {
            const response = await axios.post('/v1/auth/login', {
                email: credentials.email,
                password: credentials.password,
            });
            commit('SET_AUTH_DATA', response.data);
            return response.data;
        },
        async logout({ commit }) {
            try {
                await axios.post('/v1/auth/logout');
            } finally {
                commit('CLEAR_AUTH_DATA');
            }
        },
        async initializeAuth({ commit }) {
            commit('SET_AUTH_RESOLVED', false);
            try {
                const response = await axios.get('/v1/auth/me');
                commit('SET_AUTH_DATA', response.data);
            } catch (error) {
                commit('CLEAR_AUTH_DATA');
            }
        },
        async switchLocation({ commit }, locationid) {
            const response = await axios.post('/v1/auth/select-location', { locationid });
            commit('SET_AUTH_DATA', response.data);
            return response.data;
        },
        async fetchCompanySettings({ commit }, { force = false } = {}) {
            const settings = await getCompanySettingsCached({ force });
            commit('SET_COMPANY_SETTINGS', settings);
            return settings;
        },
    },
    getters: {
        isAuthenticated: (state) => state.isAuthenticated,
        isAuthResolved: (state) => state.authResolved,
        getUserDetails: (state) => state.userDetails,
        getPermissions: (state) => state.permissions,
        getRole: (state) => state.role,
        getCurrentLocation: (state) => state.currentLocation,
        getLocations: (state) => state.locations,
        isMultiLocationEnabled: (state) => state.multiLocationEnabled,
        getLocationMode: (state) => state.locationMode,
        getCompanySettings: (state) => state.companySettings,
        getCompanyLogoUrl: (state) => state.companySettings?.LogoUrl || null,
        isEcommerceStoreEnabled: (state) => {
            const attr = (state.userDetails || []).find((item) => item.Name === 'custom:ecommerce_store_enabled');
            return String(attr?.Value || '').toLowerCase() === 'true';
        },
        authContext: (state) => ({
            attributes: state.userDetails,
            permissions: state.permissions,
            role: state.role,
            currentLocation: state.currentLocation,
            locations: state.locations,
            multiLocationEnabled: state.multiLocationEnabled,
            locationMode: state.locationMode,
        }),
        getUserRole: (state) => getUserRole({ attributes: state.userDetails, role: state.role }),
        isAdmin: (state, getters) => getters.getUserRole === 'admin',
        hasPermission: (state, getters) => (permission) => hasPermission(getters.authContext, permission),
        canAccessRoute: (state, getters) => (routeMeta) => hasRouteAccess(getters.authContext, routeMeta),
    },
});

export default store;
